/**
 * signature-canvas.tsx — pad de firma controlado. El usuario dibuja con el
 * puntero (mouse + touch) sobre un <canvas>; al levantar el dedo/mouse el trazo
 * se exporta a un dataURL PNG (LOCAL) y se emite vía onChange. La subida a
 * Cloudinary NO ocurre acá: pasa al guardar el informe (así se puede redibujar
 * sin subir nada hasta persistir).
 *
 * Componente compartido (shared/ui): NO importa de features. value puede ser un
 * dataURL local o una URL http ya guardada — el <img> sirve a ambos.
 */
import { useEffect, useRef } from "react";
import { Eraser, Check } from "lucide-react";
import { Button } from "@/shared/ui/button.js";
import { cn } from "@/shared/lib/utils.js";

const labelClass =
  "font-mono text-[0.625rem] font-medium uppercase tracking-[0.18em] text-muted-foreground";

/** Resolución interna del canvas (px). Se escala por CSS a su contenedor. */
const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 300;

export function SignatureCanvas(props: {
  value: string;
  onChange: (dataUrl: string) => void;
  /** Mantenido por compatibilidad; ya no se sube acá, así que no se usa. */
  onError?: (msg: string) => void;
  disabled?: boolean;
  label?: string;
}) {
  const { value, onChange, disabled, label } = props;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dibujandoRef = useRef(false);
  const tieneTrazoRef = useRef(false);
  // dataURL que ya pintamos en el canvas, para no repintar (y borrar trazos
  // nuevos) cada vez que nuestro propio onChange actualiza `value`.
  const pintadoRef = useRef<string | null>(null);

  // Prepara el contexto del canvas una vez montado / al volver a dibujar.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111111";
  }, [value]);

  // Al montar/recuperar un borrador con firma local (dataURL), pintamos la firma
  // previa en el canvas para que se vea y se pueda seguir trazando. Las URLs http
  // se muestran como <img> (canvas tainted bloquea toDataURL), no acá.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const esDataUrl = value.startsWith("data:");
    if (!esDataUrl || pintadoRef.current === value) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      tieneTrazoRef.current = true;
      pintadoRef.current = value;
    };
    img.src = value;
  }, [value]);

  /** Coordenadas relativas al canvas a partir de un PointerEvent. */
  function puntoDesde(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } | null {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const escalaX = canvas.width / rect.width;
    const escalaY = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * escalaX, y: (e.clientY - rect.top) * escalaY };
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const punto = puntoDesde(e);
    if (!canvas || !ctx || !punto) return;
    canvas.setPointerCapture(e.pointerId);
    dibujandoRef.current = true;
    ctx.beginPath();
    ctx.moveTo(punto.x, punto.y);
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!dibujandoRef.current) return;
    const ctx = canvasRef.current?.getContext("2d");
    const punto = puntoDesde(e);
    if (!ctx || !punto) return;
    ctx.lineTo(punto.x, punto.y);
    ctx.stroke();
    tieneTrazoRef.current = true;
  }

  function onPointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!dibujandoRef.current) return;
    dibujandoRef.current = false;
    const canvas = canvasRef.current;
    if (canvas?.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    if (!canvas || !tieneTrazoRef.current) return;
    // Trazo → dataURL local. Se sube a Cloudinary al guardar el informe.
    const dataUrl = canvas.toDataURL("image/png");
    pintadoRef.current = dataUrl; // ya está en el canvas: el efecto no debe repintarlo
    onChange(dataUrl);
  }

  function limpiar() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    tieneTrazoRef.current = false;
    pintadoRef.current = null;
    onChange("");
  }

  // Solo mostramos el <img> para una firma YA persistida (URL http). Un dataURL
  // local recién dibujado mantiene el <canvas> montado para poder seguir
  // trazando (levantar el lápiz y continuar) sin que se reemplace por la imagen.
  const firmado = value !== "";
  const mostrarImagen = /^https?:\/\//.test(value);

  return (
    <div className="grid gap-1.5">
      {label && <span className={labelClass}>{label}</span>}
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {mostrarImagen ? (
          <div className="flex items-center justify-center bg-white p-3">
            <img src={value} alt={label ?? "Firma"} className="max-h-56 w-auto object-contain" />
          </div>
        ) : (
          <canvas
            ref={canvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            className={cn(
              "block h-52 w-full touch-none bg-white",
              disabled ? "cursor-not-allowed opacity-60" : "cursor-crosshair",
            )}
            aria-label={label ?? "Pad de firma"}
            role="img"
          />
        )}
        <div className="flex items-center justify-between border-t border-border px-3 py-2">
          <span className={cn(labelClass, "inline-flex items-center gap-1.5")}>
            {firmado ? (
              <>
                <Check className="size-3 text-brand-accent" aria-hidden />
                Firma capturada
              </>
            ) : (
              "Dibuja la firma arriba"
            )}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={limpiar}
            className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em]"
          >
            <Eraser className="size-3.5" aria-hidden />
            Limpiar
          </Button>
        </div>
      </div>
    </div>
  );
}
